/**
 * Heartbeat Manager
 * Handles WebSocket heartbeat/keepalive functionality.
 * Extracted from websocket.js for better separation of concerns.
 */

/**
 * Create a heartbeat manager
 * @param {Object} options
 * @param {number} options.interval - Heartbeat interval in ms (default 30000)
 * @param {number} options.timeout - Timeout to wait for ack in ms (default 10000)
 * @param {Function} options.send - Callback to send heartbeat message
 * @param {Function} options.onTimeout - Callback when heartbeat times out
 * @param {Function} options.log - Callback for logging (optional)
 * @returns {Object} Heartbeat manager API
 */
export function createHeartbeatManager(options) {
  const {
    interval = 30000,
    timeout = 10000,
    send,
    onTimeout,
    log = () => {},
  } = options;

  // Internal state
  let heartbeatTimer = null;
  let heartbeatTimeoutTimer = null;
  let awaitingAck = false;
  let stopped = false;

  /**
   * Send a heartbeat and start timeout timer
   */
  function sendHeartbeat() {
    if (stopped) return;

    if (awaitingAck) {
      // Previous heartbeat didn't get ack - connection is dead
      log("Heartbeat timeout - no ack received, connection dead");
      handleTimeout();
      return;
    }

    try {
      send();
      awaitingAck = true;

      // Set timeout for ack
      heartbeatTimeoutTimer = setTimeout(() => {
        if (awaitingAck && !stopped) {
          log("Heartbeat timeout - connection appears dead");
          handleTimeout();
        }
      }, timeout);
    } catch (err) {
      log(`Failed to send heartbeat: ${err.message}`);
      handleTimeout();
    }
  }

  /**
   * Handle heartbeat timeout
   */
  function handleTimeout() {
    awaitingAck = false;
    stop();
    if (onTimeout) {
      onTimeout();
    }
  }

  /**
   * Start the heartbeat timer
   */
  function start() {
    stop(); // Clear any existing timers
    stopped = false;

    heartbeatTimer = setInterval(() => {
      sendHeartbeat();
    }, interval);
  }

  /**
   * Stop all heartbeat timers
   */
  function stop() {
    stopped = true;

    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    if (heartbeatTimeoutTimer) {
      clearTimeout(heartbeatTimeoutTimer);
      heartbeatTimeoutTimer = null;
    }
    awaitingAck = false;
  }

  /**
   * Handle heartbeat ack received from server
   */
  function onAck() {
    // Ignore ack if we've already stopped (prevents stale acks)
    if (stopped) return;

    awaitingAck = false;
    if (heartbeatTimeoutTimer) {
      clearTimeout(heartbeatTimeoutTimer);
      heartbeatTimeoutTimer = null;
    }
  }

  /**
   * Check if currently awaiting an ack
   * @returns {boolean}
   */
  function isAwaitingAck() {
    return awaitingAck;
  }

  /**
   * Force an immediate heartbeat send, bypassing the interval.
   *
   * Called when we have an external signal that the socket may be stale (e.g.
   * tab returning from background after the OS suspended timers and possibly
   * killed the underlying TCP connection). Reuses sendHeartbeat()'s
   * dead-socket detection: if a previous probe is still awaiting an ack the
   * timeout path fires immediately; otherwise a fresh probe + 10s wait is
   * armed. Does not reset or skip the regular interval.
   */
  function probe() {
    if (stopped) return;
    sendHeartbeat();
  }

  return {
    start,
    stop,
    onAck,
    isAwaitingAck,
    probe,
  };
}
